import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Tracking, TrackingEvent} from '@common/tracking'
import {LinkButton} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {ExtensionActionButtons} from '@components/extensions-gallery/extension/partials/extension-action-buttons'
import {ExtensionMedia} from '@components/extensions-gallery/extension/partials/extension-media'
import {ExtensionRatingControl} from '@components/extensions-gallery/extension/partials/extension-rating-control'
import {ExtensionStatItem} from '@components/extensions-gallery/extension/partials/extension-stat-item'
import {ExtensionTags} from '@components/extensions-gallery/extension/partials/extension-tags'
import {updateRef} from '@containers/element-utils'
import {useBoolean} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {extensionItemDefaultTooltipOffset} from '../../constants'
import {handleExtensionRate} from '../../extension/extension-side-effects'
import {ExtensionStore} from '../../extensions-store'

const TwDetailsGroup = tw.div`
  py-12
`

const TwDetailsLabel = tw.div`
  uppercase
  pb-6

  text-secondary
  text-xxxs-semibold

  cursor-default
`

const TwDetailsRatingContainer = tw.div`
  flex
  gap-4
  items-baseline
  mb-12
  cursor-default
`

const TwDetailsRating = tw.span`
  text-secondary
  h2-semibold
`

const TwDetailsRatingOf = tw.span`
  text-secondary
  text-sm-regular
`

const TwDetailsDescription = tw.div`
  text-primary
  text-sm-regular
  cursor-default
`

const TwDetailsActions = tw.div<{$isDividerVisible: boolean}>`
  h-[56px]
  px-24
  py-12
  shrink-0
  shadow-grey-l60

  ${p => (p.$isDividerVisible ? 'shadow-border-b-1' : '')}
`

type ExtensionDetailsSidebarProps = {
  extension: ExtensionStore
  containerRef?: React.Ref<HTMLElement>
  onOpenSettings: (ext: ExtensionStore) => void
}

export const ExtensionDetailsSidebar = observer(
  (props: ExtensionDetailsSidebarProps) => {
    const {extension, containerRef, onOpenSettings} = props
    const isStandalone = globals.standalone

    const scrolledDown = useBoolean(false)
    const onScroll = (container: HTMLElement) => {
      if ((container.scrollTop !== 0) !== scrolledDown.value) {
        scrolledDown.setValue(container.scrollTop !== 0)
      }
    }

    return (
      <div className="flex flex-col h-full">
        {/* NOTE: fixed height is here to avoid jumping elements
            when buttons switches to loader and back */}
        <TwDetailsActions $isDividerVisible={scrolledDown.value}>
          <ExtensionActionButtons
            extensionStore={extension}
            onOpenSettings={() => onOpenSettings(extension)}
            fitWidth={true}
            className="flex-row-reverse"
          />
        </TwDetailsActions>

        <div className="min-h-0 flex-1">
          <WithPerfectScrollbar
            containerRef={x => updateRef(x, containerRef)}
            onScrollY={onScroll}
            className="h-full px-24 pb-12"
          >
            {!isStandalone && (
              <TwDetailsGroup>
                <TwDetailsLabel>
                  {Lang.msg.extensions.gallery.details.rating}
                </TwDetailsLabel>
                <TwDetailsRatingContainer>
                  <Icon icon="IconStarFilled" className="self-center" />
                  {extension.ratingsCount === 0 ? (
                    <TwDetailsRating>
                      {Lang.msg.extensions.gallery.details.noRatings}
                    </TwDetailsRating>
                  ) : (
                    <>
                      <Tooltip
                        content={Lang.msg.ratingsCount(extension.ratingsCount)}
                        offset={extensionItemDefaultTooltipOffset}
                      >
                        <TwDetailsRating>
                          {extension.rating.toFixed(1)}
                        </TwDetailsRating>
                      </Tooltip>{' '}
                      <TwDetailsRatingOf>
                        {Lang.msg.extensions.gallery.details.ratingOutOf} 5
                      </TwDetailsRatingOf>
                    </>
                  )}
                </TwDetailsRatingContainer>

                {extension.canRate && (
                  <ExtensionRatingControl
                    style="normal"
                    initialRating={extension.currentUserRating}
                    onRatingChanged={nextRating => {
                      handleExtensionRate(extension, nextRating)
                      Tracking.send(
                        TrackingEvent.extensionsRateExtensionInDetails,
                        {extensionId: extension.id}
                      )
                    }}
                  />
                )}
              </TwDetailsGroup>
            )}

            {extension.description.length > 0 && (
              <TwDetailsGroup>
                <TwDetailsLabel>
                  {Lang.msg.extensions.gallery.details.description}
                </TwDetailsLabel>
                <TwDetailsDescription>
                  {extension.description}
                </TwDetailsDescription>
              </TwDetailsGroup>
            )}

            {extension.hasTags && (
              <TwDetailsGroup>
                <TwDetailsLabel>
                  {Lang.msg.extensions.gallery.details.tags}
                </TwDetailsLabel>
                <ExtensionTags extension={extension} />
              </TwDetailsGroup>
            )}

            <TwDetailsGroup>
              <TwDetailsLabel>
                {Lang.msg.extensions.gallery.details.statistics}
              </TwDetailsLabel>
              <div className="flex gap-16">
                <ExtensionStatItem
                  icon="IconDownload"
                  value={extension.installsCount}
                  tooltipContent={Lang.msg.extensions.installsCountHint}
                />

                <ExtensionStatItem
                  icon="IconMessage"
                  value={extension.commentsCount}
                  tooltipContent={Lang.msg.extensions.commentsCountHint}
                />
              </div>
            </TwDetailsGroup>

            {extension.hasInstructionLink && (
              <TwDetailsGroup>
                <TwDetailsLabel>
                  {Lang.msg.extensions.gallery.details.documentation}
                </TwDetailsLabel>

                <a href={extension.instructionLink} target="_blank">
                  <LinkButton
                    icon="IconInstructionLink"
                    size="small"
                    noPaddings
                    iconPadding="4"
                  >
                    {Lang.msg.extensions.gallery.instruction}
                  </LinkButton>
                </a>
              </TwDetailsGroup>
            )}

            {extension.hasMedia && (
              <TwDetailsGroup>
                <TwDetailsLabel>
                  {Lang.msg.extensions.gallery.details.media}
                </TwDetailsLabel>
                <ExtensionMedia
                  direction="vertical"
                  extensionId={extension.id}
                  media={extension.media}
                  onClickMediaItem={(_, index) => {
                    Tracking.send(
                      TrackingEvent.extensionsClickOnMediaInDetails,
                      {index}
                    )
                  }}
                />
              </TwDetailsGroup>
            )}
          </WithPerfectScrollbar>
        </div>
      </div>
    )
  }
)
